import { useState } from 'react';
import SandboxModal from './components/SandboxModal';
import HumanFactorModal from './components/HumanFactorModal';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [sandboxOpen, setSandboxOpen] = useState(false);
  const [humanFactorOpen, setHumanFactorOpen] = useState(false);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "Will the bot access my computer?",
      answer: "No.\nIt runs inside a protected container.\nNo unrestricted device or system access."
    },
    {
      question: "Can it act on its own?",
      answer: "Only within documented permissions.\nExecution logic is predefined."
    },
    {
      question: "What prevents compromise?",
      answer: "Isolation.\nControlled credentials.\nRestricted integrations.\n\nRisk is reduced structurally."
    },
    {
      question: "Will this replace my team?",
      answer: "No.\nAI increases leverage.\nHumans retain authority."
    },
    {
      question: "I'm not technical.",
      answer: "You don't need to be.\nYou manage outcomes.\nWe manage the infrastructure."
    },
    {
      question: "Why HumanFactor?",
      answer: "The Sandbox provides containment.\n\nHumanFactor provides architecture and governance.\n\nOwnership is the difference."
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-stone-50/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center">
              <img src="https://public.readdy.ai/ai/img_res/8e33750e-f5da-4a55-98cf-da107ee2bdef.png" alt="Humanclaw.io" className="w-full h-full object-contain" />
            </div>
            <span className="text-lg font-medium text-stone-900">Humanclaw.io</span>
          </div>
          <button
            onClick={() => setSandboxOpen(true)}
            className="px-6 py-2 bg-stone-900 text-stone-50 text-sm font-medium hover:bg-stone-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Enter the Sandbox
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs uppercase tracking-widest text-stone-500 mb-8">
            For professionals who are AI-curious. Not AI-reckless.
          </p>
          
          <h1 className="text-7xl md:text-8xl font-serif text-stone-900 mb-8 leading-tight">
            AI, <span className="text-red-700">On a Leash.</span>
          </h1>
          
          <p className="text-2xl text-stone-700 mb-16 font-light">
            Intelligence is powerful.<br />
            Bots require control.
          </p>

          <div className="text-left max-w-2xl mx-auto space-y-6 text-stone-700 leading-relaxed mb-16">
            <p>The internet debates AI.</p>
            <p>Professionals worry about bots.</p>
            
            <div className="pl-6 border-l-2 border-stone-300 space-y-2">
              <p>Unsupervised execution.</p>
              <p>Permission drift.</p>
              <p>Security exposure.</p>
            </div>

            <p className="font-medium text-stone-900"><span className="text-red-700">We made it wait.</span></p>

            <div className="space-y-2">
              <p>We sandbox the AI.</p>
              <p>We contain the bot.</p>
              <p>We define authority.</p>
              <p>We supervise execution.</p>
            </div>

            <p>It behaves like infrastructure.<br />Not an experiment.</p>

            <div className="space-y-2">
              <p>More leverage.</p>
              <p>Less repetition.</p>
              <p><span className="text-red-700">No operational drift.</span></p>
            </div>

            <p className="font-medium text-stone-900">Technology for the non-technical.</p>

            <p>Designed for operators, not hobbyists.</p>

            <p>Built on governance principles used in enterprise environments.</p>

            <p className="font-medium text-stone-900"><span className="text-red-700">Human first.</span> Machine powered.</p>
          </div>

          <button
            onClick={() => setSandboxOpen(true)}
            className="px-10 py-4 bg-stone-900 text-stone-50 text-base font-medium hover:bg-stone-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Enter the Sandbox
          </button>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* The Sandbox */}
            <div className="border-2 border-stone-900 p-10">
              <div className="flex items-start justify-between mb-6">
                <h3 className="text-2xl font-medium text-stone-900">The Sandbox</h3>
                <div className="w-12 h-12 flex items-center justify-center">
                  <img src="https://public.readdy.ai/ai/img_res/efec355b-5ca1-4dd7-af40-2bed1be2913f.png" alt="The Sandbox" className="w-full h-full object-contain" />
                </div>
              </div>
              
              <p className="text-4xl font-light text-stone-900 mb-8">$699 <span className="text-lg text-stone-600">/ month</span></p>
              
              <div className="space-y-4 mb-8 text-stone-700">
                <p className="font-medium text-stone-900">Contained AI.<br />Governed bot execution.</p>
                
                <div className="space-y-2">
                  <p>Isolated.</p>
                  <p>Useful.</p>
                  <p>Predictable.</p>
                </div>
                
                <p className="pt-4">Explore AI without exposing your systems.</p>
              </div>
            </div>

            {/* HumanFactor */}
            <div className="border-2 border-stone-900 p-10 bg-stone-900 text-stone-50">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-medium mb-2">HumanFactor</h3>
                  <p className="text-sm text-stone-300">Private Operating Retainer</p>
                </div>
                <div className="w-12 h-12 flex items-center justify-center">
                  <img src="https://public.readdy.ai/ai/img_res/c8f9a687-76f7-40b2-93ce-0e59bd7f6b5f.png" alt="HumanFactor" className="w-full h-full object-contain" />
                </div>
              </div>
              
              <p className="text-4xl font-light mb-2">$999 <span className="text-lg text-stone-300">/ build</span></p>
              <p className="text-sm text-stone-300 mb-8">Weekly Optimization Included</p>
              
              <div className="space-y-4 mb-8">
                <p className="font-medium">Architected AI layer.<br />Defined permissions.<br />Supervised behavior.</p>
                
                <p className="font-medium pt-4">Includes:</p>
                
                <ul className="space-y-2 text-stone-200">
                  <li>• Workflow architecture</li>
                  <li>• Escalation logic</li>
                  <li>• Bot containment</li>
                  <li>• Weekly optimization</li>
                  <li>• Human oversight</li>
                </ul>
                
                <p className="pt-4 font-medium">This is governed automation.</p>
                
                <p className="text-stone-300">Containment reduces risk.<br />Oversight preserves authority.</p>
              </div>
            </div>
          </div>

          <p className="text-center text-sm text-stone-600 mt-12 tracking-wide">
            Contain first.<br />Scale deliberately.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-6 bg-stone-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-serif text-stone-900 text-center mb-20">
            Architecture Before <span className="text-red-700">Automation</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <h3 className="text-xl font-medium text-red-700 mb-4">Contain</h3>
              <div className="space-y-3 text-stone-700">
                <p>AI operates inside a sandboxed environment.</p>
                <p>No open system access.</p>
                <p>No uncontrolled integrations.</p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-medium text-red-700 mb-4">Define</h3>
              <div className="space-y-3 text-stone-700">
                <p>We document authority.</p>
                <p className="pt-2">What it drafts.</p>
                <p>What it executes.</p>
                <p>When it escalates.</p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-medium text-red-700 mb-4">Supervise</h3>
              <div className="space-y-3 text-stone-700">
                <p>Permissions do not expand silently.</p>
                <p>Automation does not self-authorize.</p>
                <p className="pt-2">Governance is continuous.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-serif text-stone-900 text-center mb-16">
            Questions, Answered.
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-stone-200">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full py-6 flex items-center justify-between text-left cursor-pointer hover:text-stone-600 transition-colors"
                >
                  <span className="text-lg font-medium text-stone-900">{faq.question}</span>
                  <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-xl text-stone-600`}></i>
                </button>
                {openFaq === index && (
                  <div className="pb-6 text-stone-700 whitespace-pre-line leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-6 bg-stone-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-5xl font-serif text-stone-900 mb-6">
            Choose Your <span className="text-red-700">Box.</span>
          </h2>
          
          <p className="text-stone-700 mb-12 leading-relaxed">
            Contained.<br />
            Or architected.<br /><br />
            <span className="text-red-700 font-medium">Control remains with you.</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setSandboxOpen(true)}
              className="px-10 py-4 bg-stone-900 text-stone-50 text-base font-medium hover:bg-stone-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Enter the Sandbox
            </button>
            <button
              onClick={() => setHumanFactorOpen(true)}
              className="px-10 py-4 border-2 border-stone-900 text-stone-900 text-base font-medium hover:bg-stone-900 hover:text-stone-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Activate HumanFactor
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-white border-t border-stone-200">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-8 flex items-center justify-center">
              <img src="https://public.readdy.ai/ai/img_res/d580fb0e-2d9a-4c34-9433-3454b36b0bde.png" alt="Humanclaw.io" className="w-full h-full object-contain" />
            </div>
            <span className="text-lg font-medium text-stone-900">Humanclaw.io</span>
          </div>
          <p className="text-stone-600 mb-4">
            Human First. Machine Powered.
          </p>
          <p className="text-sm text-stone-500">
            <a href="https://readdy.ai/?ref=logo" className="hover:text-stone-700 transition-colors cursor-pointer">Website Builder</a>
          </p>
        </div>
      </footer>

      <SandboxModal isOpen={sandboxOpen} onClose={() => setSandboxOpen(false)} />
      <HumanFactorModal isOpen={humanFactorOpen} onClose={() => setHumanFactorOpen(false)} />
    </div>
  );
}
